import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, HStack, rem, Switch, Text, useDisclosure } from '@bonusly/design-system';

import { getSeasonConfig } from './get-season-config';
import { useSeasonalTheme } from './seasonal-theme-context';
import { SeasonalThemeModal } from './seasonal-theme-modal';

interface SeasonalToggleProps {
	isExpanded?: boolean;
}

export const SeasonalToggle: FC<SeasonalToggleProps> = ({ isExpanded = true }) => {
	const { t } = useTranslation('components');
	const { hasSeasonalThemePermission, isSeasonalThemeEnabled, toggleTheme } = useSeasonalTheme();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const seasonConfig = getSeasonConfig();

	// Only show toggle if user has permission (seasonal-themes feature flag) and sidebar is expanded
	if (!hasSeasonalThemePermission || !isExpanded) {
		return null;
	}

	const handleToggle = () => {
		if (isSeasonalThemeEnabled) {
			// If enabled, just toggle it off
			toggleTheme();
		} else {
			// If disabled, show the modal first
			onOpen();
		}
	};

	const labelId = 'seasonal-theme-label';

	return (
		<>
			<HStack
				as="label"
				htmlFor="seasonal-theme-toggle"
				w="100%"
				justifyContent="space-between"
				alignItems="center"
				spacing={3}
				py={rem(8)}
				px={rem(10)}
				borderRadius="md"
				bg="transparent"
				cursor="pointer"
				transition="all 0.15s cubic-bezier(.4, 0, .2, 1)"
				_hover={{
					bg: 'secondary.bgAlpha',
				}}
				_focusWithin={{ outline: '2px solid', outlineColor: 'border.focus', outlineOffset: '-2px' }}
			>
				<HStack spacing={3} flex={1} minW={0} alignItems="center">
					<Box w={6} h={6} display="flex" alignItems="center" justifyContent="center" flexShrink={0} overflow="visible" aria-hidden="true">
						<Text as="span" fontSize="2xl" lineHeight={1} display="block">
							{seasonConfig.toggleEmoji}
						</Text>
					</Box>
					<Text id={labelId} fontSize={100} fontWeight="medium" whiteSpace="nowrap" userSelect="none">
						{t('seasonal_theme.toggle.label', { defaultValue: 'Holiday theme' })}
					</Text>
				</HStack>
				<Switch
					id="seasonal-theme-toggle"
					size="sm"
					flexShrink={0}
					isChecked={isSeasonalThemeEnabled}
					onChange={handleToggle}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault();
							handleToggle();
						}
					}}
					colorScheme="blue"
					aria-labelledby={labelId}
					sx={{
						'& .chakra-switch__track': {
							_focusVisible: {
								boxShadow: 'none',
							},
						},
					}}
				/>
			</HStack>

			<SeasonalThemeModal isOpen={isOpen} onClose={onClose} onEnable={toggleTheme} />
		</>
	);
};
